import type { SkinConfig, SnakeVisualConfig } from "../config/gameplay-config";
import {
  createSnakeHeadOutline,
  projectSnakeHeadFeature,
} from "../config/snake-head-geometry";

export interface SnakeVisualDrawingSurface {
  beginPath(): unknown;
  closePath(): unknown;
  fillCircle(x: number, y: number, radius: number): unknown;
  fillPath(): unknown;
  fillRect(x: number, y: number, width: number, height: number): unknown;
  fillStyle(color: number, alpha?: number): unknown;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): unknown;
  lineStyle(lineWidth: number, color: number, alpha?: number): unknown;
  lineTo(x: number, y: number): unknown;
  moveTo(x: number, y: number): unknown;
  strokeCircle(x: number, y: number, radius: number): unknown;
  strokePath(): unknown;
}

export interface SnakeVisualPoint {
  readonly x: number;
  readonly y: number;
}

export interface SnakeVisualDirection {
  readonly forwardX: number;
  readonly forwardY: number;
  readonly sideX: number;
  readonly sideY: number;
}

export interface SnakeHeadVisual {
  readonly accentColor: number;
  readonly bodyColor: number;
  readonly directionRadians: number;
  readonly face: SnakeFacePose;
  readonly pattern: SkinConfig["pattern"] | null;
  readonly position: SnakeVisualPoint;
  readonly radius: number;
}

export interface SnakeFacePose {
  readonly eyesClosed: boolean;
  readonly mouthOpenScale: number;
  readonly tongueLengthScale: number;
}

export interface SnakeBodyVisualSegment {
  readonly position: SnakeVisualPoint;
  readonly radius: number;
}

export interface SnakeBodyCrossSection {
  readonly left: SnakeVisualPoint;
  readonly position: SnakeVisualPoint;
  readonly radius: number;
  readonly right: SnakeVisualPoint;
}

export interface BotBandPattern {
  readonly interval: 3 | 4 | 5;
  readonly phase: 0 | 1 | 2;
}

export const getSnakeVisualDirection = (
  leading: SnakeVisualPoint,
  trailing: SnakeVisualPoint,
  fallbackRadians: number,
): SnakeVisualDirection => {
  const deltaX = leading.x - trailing.x;
  const deltaY = leading.y - trailing.y;
  const distance = Math.hypot(deltaX, deltaY);
  const forwardX =
    distance === 0 ? Math.cos(fallbackRadians) : deltaX / distance;
  const forwardY =
    distance === 0 ? Math.sin(fallbackRadians) : deltaY / distance;
  return { forwardX, forwardY, sideX: -forwardY, sideY: forwardX };
};

export const getBotBandPattern = (botId: string): BotBandPattern => {
  const hash = Array.from(botId).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  const patternIndex = hash % 3;
  if (patternIndex === 0) {
    return { interval: 3, phase: 0 };
  }
  if (patternIndex === 1) {
    return { interval: 4, phase: 1 };
  }
  return { interval: 5, phase: 2 };
};

const getSnakeBodyDirection = (
  points: readonly SnakeBodyVisualSegment[],
  point: SnakeBodyVisualSegment,
  index: number,
  fallbackRadians: number,
): SnakeVisualDirection => {
  const leading =
    points[index - 1]?.position ??
    ({
      x: point.position.x + Math.cos(fallbackRadians),
      y: point.position.y + Math.sin(fallbackRadians),
    } satisfies SnakeVisualPoint);
  const trailing = points[index + 1]?.position ?? point.position;
  return getSnakeVisualDirection(leading, trailing, fallbackRadians);
};

const getSnakeBodyCrossSection = (
  points: readonly SnakeBodyVisualSegment[],
  point: SnakeBodyVisualSegment,
  index: number,
  fallbackRadians: number,
): SnakeBodyCrossSection => {
  const direction = getSnakeBodyDirection(
    points,
    point,
    index,
    fallbackRadians,
  );
  return {
    left: {
      x: point.position.x + direction.sideX * point.radius,
      y: point.position.y + direction.sideY * point.radius,
    },
    position: point.position,
    radius: point.radius,
    right: {
      x: point.position.x - direction.sideX * point.radius,
      y: point.position.y - direction.sideY * point.radius,
    },
  };
};

export const getSnakeBodyCrossSections = (
  head: Pick<SnakeHeadVisual, "position" | "radius">,
  segments: readonly SnakeBodyVisualSegment[],
  fallbackRadians: number,
  visual: SnakeVisualConfig,
): readonly [SnakeBodyCrossSection, ...SnakeBodyCrossSection[]] => {
  const points: readonly [SnakeBodyVisualSegment, ...SnakeBodyVisualSegment[]] =
    [
      {
        position: head.position,
        radius: head.radius * visual.neckRadiusScale,
      },
      ...segments,
    ];
  const [headPoint, ...bodyPoints] = points;
  return [
    getSnakeBodyCrossSection(points, headPoint, 0, fallbackRadians),
    ...bodyPoints.map((point, index) =>
      getSnakeBodyCrossSection(points, point, index + 1, fallbackRadians),
    ),
  ];
};

export const drawSnakeBody = (
  graphics: SnakeVisualDrawingSurface,
  head: Pick<SnakeHeadVisual, "position" | "radius">,
  segments: readonly SnakeBodyVisualSegment[],
  bodyColor: number,
  fallbackRadians: number,
  visual: SnakeVisualConfig,
): void => {
  const sections = getSnakeBodyCrossSections(
    head,
    segments,
    fallbackRadians,
    visual,
  );
  graphics.fillStyle(bodyColor, 1);
  let leading = sections[0];
  for (const trailing of sections.slice(1)) {
    graphics.fillTriangle(
      leading.left.x,
      leading.left.y,
      leading.right.x,
      leading.right.y,
      trailing.left.x,
      trailing.left.y,
    );
    graphics.fillTriangle(
      leading.right.x,
      leading.right.y,
      trailing.right.x,
      trailing.right.y,
      trailing.left.x,
      trailing.left.y,
    );
    leading = trailing;
  }
  const first = sections[0];
  graphics.lineStyle(
    Math.max(2, head.radius * visual.outlineWidthScale),
    visual.outlineColor,
    1,
  );
  graphics.beginPath();
  graphics.moveTo(first.left.x, first.left.y);
  for (const section of sections.slice(1)) {
    graphics.lineTo(section.left.x, section.left.y);
  }
  for (const section of [...sections].reverse()) {
    graphics.lineTo(section.right.x, section.right.y);
  }
  graphics.lineTo(first.left.x, first.left.y);
  graphics.strokePath();
};

export const drawBotBand = (
  graphics: SnakeVisualDrawingSurface,
  position: SnakeVisualPoint,
  radius: number,
  direction: SnakeVisualDirection,
  accentColor: number,
  index: number,
  pattern: BotBandPattern,
): void => {
  if ((index + pattern.phase) % pattern.interval !== 0) {
    return;
  }
  graphics.lineStyle(radius * 0.3, accentColor, 0.86);
  graphics.beginPath();
  graphics.moveTo(
    position.x + direction.sideX * radius * 0.78,
    position.y + direction.sideY * radius * 0.78,
  );
  graphics.lineTo(
    position.x - direction.sideX * radius * 0.78,
    position.y - direction.sideY * radius * 0.78,
  );
  graphics.strokePath();
};

export const drawSnakePattern = (
  graphics: SnakeVisualDrawingSurface,
  position: SnakeVisualPoint,
  radius: number,
  direction: SnakeVisualDirection,
  bodyColor: number,
  accentColor: number,
  pattern: SkinConfig["pattern"],
  index: number,
): void => {
  const phase = index % 4;
  const sideSign = phase % 2 === 0 ? 1 : -1;
  if (pattern === "leaf") {
    graphics.fillStyle(accentColor, 0.9);
    graphics.fillTriangle(
      position.x + direction.forwardX * radius * 0.46,
      position.y + direction.forwardY * radius * 0.46,
      position.x -
        direction.forwardX * radius * 0.34 +
        direction.sideX * radius * 0.22,
      position.y -
        direction.forwardY * radius * 0.34 +
        direction.sideY * radius * 0.22,
      position.x -
        direction.forwardX * radius * 0.34 -
        direction.sideX * radius * 0.22,
      position.y -
        direction.forwardY * radius * 0.34 -
        direction.sideY * radius * 0.22,
    );
  } else if (pattern === "spots") {
    graphics.fillStyle(accentColor, 0.92);
    graphics.fillCircle(
      position.x + direction.sideX * radius * 0.32 * sideSign,
      position.y + direction.sideY * radius * 0.32 * sideSign,
      radius * (phase < 2 ? 0.18 : 0.13),
    );
  } else if (pattern === "stripes") {
    graphics.lineStyle(radius * 0.28, accentColor, 0.92);
    graphics.beginPath();
    graphics.moveTo(
      position.x + direction.sideX * radius * 0.7,
      position.y + direction.sideY * radius * 0.7,
    );
    graphics.lineTo(
      position.x - direction.sideX * radius * 0.7,
      position.y - direction.sideY * radius * 0.7,
    );
    graphics.strokePath();
  } else if (pattern === "ripples") {
    graphics.lineStyle(radius * 0.12, accentColor, 0.9);
    graphics.strokeCircle(
      position.x + direction.forwardX * radius * (phase - 1.5) * 0.12,
      position.y + direction.forwardY * radius * (phase - 1.5) * 0.12,
      radius * 0.42,
    );
  } else if (pattern === "confetti") {
    graphics.fillStyle(accentColor, 0.95);
    graphics.fillRect(
      position.x - radius * 0.25,
      position.y + radius * 0.24 * sideSign,
      radius * 0.5,
      radius * 0.18,
    );
  } else if (pattern === "loops") {
    graphics.lineStyle(radius * 0.13, accentColor, 0.9);
    graphics.strokeCircle(
      position.x + direction.sideX * radius * 0.2 * sideSign,
      position.y + direction.sideY * radius * 0.2 * sideSign,
      radius * 0.36,
    );
  } else if (pattern === "freckles") {
    graphics.fillStyle(accentColor, 0.95);
    graphics.fillCircle(
      position.x +
        direction.forwardX * radius * 0.18 +
        direction.sideX * radius * 0.28 * sideSign,
      position.y +
        direction.forwardY * radius * 0.18 +
        direction.sideY * radius * 0.28 * sideSign,
      radius * 0.1,
    );
    graphics.fillCircle(
      position.x -
        direction.forwardX * radius * 0.24 -
        direction.sideX * radius * 0.12 * sideSign,
      position.y -
        direction.forwardY * radius * 0.24 -
        direction.sideY * radius * 0.12 * sideSign,
      radius * 0.07,
    );
  } else {
    graphics.fillStyle(accentColor, 0.95);
    graphics.fillCircle(position.x, position.y, radius * 0.48);
    graphics.fillStyle(bodyColor, 1);
    graphics.fillCircle(
      position.x + direction.forwardX * radius * 0.24,
      position.y + direction.forwardY * radius * 0.24,
      radius * 0.44,
    );
  }
};

export const drawSnakeTailPoint = (
  graphics: SnakeVisualDrawingSurface,
  leading: SnakeVisualPoint,
  tail: SnakeVisualPoint,
  radius: number,
  bodyColor: number,
  fallbackRadians: number,
  visual: SnakeVisualConfig,
): void => {
  const direction = getSnakeVisualDirection(leading, tail, fallbackRadians);
  const tip = {
    x: tail.x - direction.forwardX * radius * visual.tailPointScale,
    y: tail.y - direction.forwardY * radius * visual.tailPointScale,
  };
  graphics.fillStyle(visual.outlineColor, 1);
  graphics.fillTriangle(
    tip.x,
    tip.y,
    tail.x +
      direction.sideX *
        radius *
        (visual.tailBaseScale + visual.outlineWidthScale),
    tail.y +
      direction.sideY *
        radius *
        (visual.tailBaseScale + visual.outlineWidthScale),
    tail.x -
      direction.sideX *
        radius *
        (visual.tailBaseScale + visual.outlineWidthScale),
    tail.y -
      direction.sideY *
        radius *
        (visual.tailBaseScale + visual.outlineWidthScale),
  );
  graphics.fillCircle(
    tip.x,
    tip.y,
    radius * (visual.tailTipRadiusScale + visual.outlineWidthScale),
  );
  graphics.fillStyle(bodyColor, 1);
  graphics.fillTriangle(
    tip.x,
    tip.y,
    tail.x + direction.sideX * radius * visual.tailBaseScale,
    tail.y + direction.sideY * radius * visual.tailBaseScale,
    tail.x - direction.sideX * radius * visual.tailBaseScale,
    tail.y - direction.sideY * radius * visual.tailBaseScale,
  );
  graphics.fillCircle(tip.x, tip.y, radius * visual.tailTipRadiusScale);
};

export const drawSnakeTongue = (
  graphics: SnakeVisualDrawingSurface,
  position: SnakeVisualPoint,
  radius: number,
  direction: SnakeVisualDirection,
  visual: SnakeVisualConfig,
  lengthScale = 1,
): void => {
  if (lengthScale <= 1) {
    return;
  }
  const start = {
    x: position.x + direction.forwardX * radius * visual.headLengthScale,
    y: position.y + direction.forwardY * radius * visual.headLengthScale,
  };
  const fork = {
    x:
      start.x +
      direction.forwardX * radius * visual.tongueLengthScale * lengthScale,
    y:
      start.y +
      direction.forwardY * radius * visual.tongueLengthScale * lengthScale,
  };
  const tipForwardScale = visual.tongueForkScale * 0.72;
  const tipSideScale = visual.tongueForkScale * 0.48;
  graphics.lineStyle(Math.max(2, radius * 0.07), 0xff7a90, 1);
  graphics.beginPath();
  graphics.moveTo(start.x, start.y);
  graphics.lineTo(fork.x, fork.y);
  for (const sideSign of [-1, 1]) {
    graphics.moveTo(fork.x, fork.y);
    graphics.lineTo(
      fork.x +
        direction.forwardX * radius * tipForwardScale +
        direction.sideX * radius * tipSideScale * sideSign,
      fork.y +
        direction.forwardY * radius * tipForwardScale +
        direction.sideY * radius * tipSideScale * sideSign,
    );
  }
  graphics.strokePath();
};

const drawSnakeHeadSurface = (
  graphics: SnakeVisualDrawingSurface,
  snake: SnakeHeadVisual,
  visual: SnakeVisualConfig,
  direction: SnakeVisualDirection,
): void => {
  const outline = createSnakeHeadOutline(
    snake.position,
    snake.radius,
    direction,
    visual,
  );
  graphics.fillStyle(snake.bodyColor, 1);
  graphics.beginPath();
  outline.forEach((point, index) => {
    if (index === 0) {
      graphics.moveTo(point.x, point.y);
    } else {
      graphics.lineTo(point.x, point.y);
    }
  });
  graphics.closePath();
  graphics.fillPath();
  graphics.lineStyle(
    Math.max(2, snake.radius * visual.outlineWidthScale),
    visual.outlineColor,
    1,
  );
  graphics.beginPath();
  outline.forEach((point, index) => {
    if (index === 0) {
      graphics.moveTo(point.x, point.y);
    } else {
      graphics.lineTo(point.x, point.y);
    }
  });
  graphics.strokePath();
};

export const drawSnakeHead = (
  graphics: SnakeVisualDrawingSurface,
  snake: SnakeHeadVisual,
  visual: SnakeVisualConfig,
): void => {
  const direction = getSnakeVisualDirection(
    {
      x: snake.position.x + Math.cos(snake.directionRadians),
      y: snake.position.y + Math.sin(snake.directionRadians),
    },
    snake.position,
    snake.directionRadians,
  );
  drawSnakeHeadSurface(graphics, snake, visual, direction);
  if (snake.pattern !== null) {
    drawSnakePattern(
      graphics,
      snake.position,
      snake.radius * 0.62,
      direction,
      snake.bodyColor,
      snake.accentColor,
      snake.pattern,
      0,
    );
  }
  for (const sideSign of [-1, 1]) {
    const eye = projectSnakeHeadFeature(
      snake.position,
      snake.radius,
      direction,
      visual.eyeForwardScale,
      visual.eyeSideScale * sideSign,
    );
    if (snake.face.eyesClosed) {
      graphics.lineStyle(Math.max(2, snake.radius * 0.08), 0x102a43, 1);
      graphics.beginPath();
      graphics.moveTo(
        eye.x + direction.sideX * snake.radius * visual.eyeRadiusScale,
        eye.y + direction.sideY * snake.radius * visual.eyeRadiusScale,
      );
      graphics.lineTo(
        eye.x - direction.sideX * snake.radius * visual.eyeRadiusScale,
        eye.y - direction.sideY * snake.radius * visual.eyeRadiusScale,
      );
      graphics.strokePath();
    } else {
      graphics.fillStyle(visual.outlineColor, 1);
      graphics.fillCircle(eye.x, eye.y, snake.radius * visual.eyeRadiusScale);
      graphics.fillStyle(0xf7fff7, 1);
      graphics.fillCircle(
        eye.x +
          direction.forwardX * snake.radius * visual.eyeHighlightForwardScale,
        eye.y +
          direction.forwardY * snake.radius * visual.eyeHighlightForwardScale,
        snake.radius * visual.eyeHighlightRadiusScale,
      );
    }
  }
  const mouth = projectSnakeHeadFeature(
    snake.position,
    snake.radius,
    direction,
    visual.mouthForwardScale,
    0,
  );
  if (snake.face.mouthOpenScale > 0) {
    graphics.fillStyle(0x102a43, 0.95);
    graphics.fillTriangle(
      mouth.x + direction.forwardX * snake.radius * 0.18,
      mouth.y + direction.forwardY * snake.radius * 0.18,
      mouth.x + direction.sideX * snake.radius * snake.face.mouthOpenScale,
      mouth.y + direction.sideY * snake.radius * snake.face.mouthOpenScale,
      mouth.x - direction.sideX * snake.radius * snake.face.mouthOpenScale,
      mouth.y - direction.sideY * snake.radius * snake.face.mouthOpenScale,
    );
  } else {
    graphics.lineStyle(Math.max(2, snake.radius * 0.08), 0x102a43, 0.9);
    graphics.beginPath();
    graphics.moveTo(
      mouth.x + direction.sideX * snake.radius * 0.12,
      mouth.y + direction.sideY * snake.radius * 0.12,
    );
    graphics.lineTo(
      mouth.x - direction.sideX * snake.radius * 0.12,
      mouth.y - direction.sideY * snake.radius * 0.12,
    );
    graphics.strokePath();
  }
  drawSnakeTongue(
    graphics,
    snake.position,
    snake.radius,
    direction,
    visual,
    snake.face.tongueLengthScale,
  );
};
